import React, { useCallback } from 'react'
import { Redirect } from 'react-router-dom'
import useForm from '../../../../hooks/useForm'
import { convertToYYYYMMDD } from '../../../../lib/date'
import Banner from '../../../atoms/banner/Banner'
import Button from '../../../atoms/button/Button'
import Datepicker from '../../../atoms/datepicker/Datepicker'
import DisplayField from '../../../atoms/form/display-field/DisplayField'
import Form, { FormRequiredText } from '../../../atoms/form/Form'
import Input from '../../../atoms/form/input/Input'
import InlineGroup from '../../../atoms/inlinegroup/InlineGroup'
import VerticalGroup from '../../../atoms/verticalgroup/VerticalGroup'
import ClientDropdown from '../../../organisms/client-dropdown/ClientDropdown'
import EventSchedulingSummary from '../event-scheduling/EventSchedulingSummary'
import SelectAssessment from './SelectAssessment'
import SelectBranding from './SelectBranding'
import SelectSimulation from './SelectSimulation'

interface Props {
  event?: API.EventResponseSummary
  onSave: (data: API.EventRequest) => Promise<API.EventResponse>
  onCancel: () => void
}

function EventForm({ event, onSave, onCancel }: Props) {
  const [{
    formData: {
      date,
      clientId,
      name,
      location,
      streamName,
      brandingId,
      simulationId,
      simulation,
      assessmentId,
      schedule
    },
    inProgress,
    fieldErrors,
    error,
    data,
    completed,
  }, { setField, setDateField, setDropdownField, handleSubmit, setFieldRaw }] = useForm(event ? {
    ...event,
    date: new Date(event.date)
  } : {
    date: new Date(),
    clientId: '',
    brandingId: null,
    simulationId: null,
    assessmentId: null,
    name: '',
    location: '',
    streamName: null,
    schedule: null,
    simulation: null,
  })

  const callback = useCallback(() => {
    const payload = {
      date: convertToYYYYMMDD(date),
      clientId,
      brandingId,
      simulationId,
      assessmentId,
      name: name.trim(),
      location: location.trim(),
      streamName: streamName?.trim() ?? null,
      schedule
    }
    return onSave(payload)

  }, [date, clientId, brandingId, name, location, streamName, simulationId, assessmentId, schedule, onSave])

  const validate = useCallback(() => {
    const errors = []
    if (!clientId) {
      errors.push({ field: 'clientId', message: 'Select a client' })
    }
    if (!name) {
      errors.push({ field: 'name', message: 'Enter an event name' })
    }
    if (!location) {
      errors.push({ field: 'location', message: 'Enter a location' })
    }
    return errors
  }, [clientId, name, location])

  const onBrandingSelected = useCallback((branding: API.BrandingResponse) => {
    setFieldRaw('brandingId', branding.id)
  }, [setFieldRaw])

  const onBrandingCleared = useCallback(() => {
    setFieldRaw('brandingId', null)
  }, [setFieldRaw])

  const onSimulationChanged = useCallback((simulation: API.SimulationResponse | null) => {
    setFieldRaw('simulationId', simulation ? simulation.id : null)
    setFieldRaw('simulation', simulation)
  }, [setFieldRaw])

  const onAssessmentChanged = useCallback((assessment: API.AssessmentResponse | null) => {
    setFieldRaw('assessmentId', assessment ? assessment.id : null)
  }, [setFieldRaw])

  const onClientChange = useCallback(({ label, value }: { label: string, value: string }) => {
    setDropdownField('clientId')({ label, value })
    onBrandingCleared()
    setFieldRaw('simulationId', null)
    setFieldRaw('simulation', null)
  }, [setDropdownField, onBrandingCleared, setFieldRaw])

  const onSchedulingChange = useCallback((schedule) => {
    if (!!event && event.id) {
      onSave({
        date: event.date,
        clientId: event.clientId,
        brandingId: event.brandingId,
        simulationId: event.simulationId,
        name: event.name,
        location: event.location,
        streamName: event.streamName,
        schedule
      })
    }
    setFieldRaw('schedule', schedule)
  }, [event, onSave, setFieldRaw])

  return (
    <>
      <VerticalGroup spread full >
        <VerticalGroup wide>
          <Banner type='error' active={!!error} message={error?.message} />
          {!!completed && !error && process.env.REACT_APP_USE_NEW_EVENT_UI !== 'true' && <Redirect to={`/events/${data.id}/view`} />}
          {!!completed && !error && process.env.REACT_APP_USE_NEW_EVENT_UI === 'true' && <Redirect to={`/events/${data.id}/view-wizard`} />}
          <Form id='event-add-form' onSubmit={handleSubmit(callback, validate)}>
            <FormRequiredText />
            <Datepicker label='Date *' autoFocus={!event} date={date} onChange={setDateField('date')} error={fieldErrors.date} />
            <ClientDropdown label='Client *' clientId={clientId} onChange={onClientChange} error={fieldErrors.clientId} />
            <Input autoFocus={!!event} data-test='name' type='text' label='Event Name *' value={name} onChange={setField('name')} error={fieldErrors.name} />
            <Input data-test='location' type='text' label='Location *' value={location} onChange={setField('location')} error={fieldErrors.location} />
            <Input data-test='streamName' type='text' label='Stream Name' value={streamName || ''} onChange={setField('streamName')} error={fieldErrors.streamName} />
            {!!event && event.id && <DisplayField data-test='event-id' label='Event ID' value={event.id} />}
          </Form>
        </VerticalGroup>
        <VerticalGroup wide spaceBetweenElements={4}>
          <h2>Simulation Settings</h2>
          <SelectBranding clientId={clientId} brandingId={brandingId} onBrandingSelected={onBrandingSelected} onUnselectBranding={onBrandingCleared} />
          <SelectSimulation clientId={clientId} simulationId={simulationId} onChange={onSimulationChanged} />
          <EventSchedulingSummary schedule={schedule} simulation={simulation} onSave={onSchedulingChange}/>
          <SelectAssessment clientId={clientId} assessmentId={assessmentId} onChange={onAssessmentChanged} />
        </VerticalGroup>
        <InlineGroup block spread className='mt-3'>
          <Button data-test='cancel' light wide onClick={onCancel}>Cancel</Button>
          <Button
            data-test='save'
            type='submit'
            form='event-add-form'
            wide
            inProgress={inProgress}
          >Save
          </Button>
        </InlineGroup>
      </VerticalGroup>
    </>
  )
}

export default EventForm
